<script setup>
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import {
  MapCanvas,
  MapSource,
  MapLayer,
  DialogCreate,
  MapProjectLayer,
  MapProjectBoundaries,
  MapDraw,
} from '@/components/map'

const props = defineProps({
  activeTab: {
    type: String,
    default: 'projects'
  }
})

const route = useRoute()
const projectId = computed(() => parseInt(route.params.project_id))

const store = useStore()
const orgId = computed(() => store.getters['org/getSelected'])

// The single canvas renders the Default Map's basemap. A stable fallback keeps the
// MapCanvas prop populated before any maps are seeded.
const activeMap = computed(() =>
  store.getters['maps/getDefaultMap'](orgId.value) ||
  { id: '__default__', name: 'Map', basemap: 'outdoors', layerCodes: [] }
)

const projects = computed(() => store.getters['project/getProjects'])
const flatedProjects = computed(() => store.getters['project/getFlatedProjects'])

const projectsToRender = computed(() => {
  if (!projectId.value) {
    return projects.value
  } else {
    const currentProject = flatedProjects.value.find((project) => project.project_id === projectId.value)
    if (!currentProject) return null
    if (currentProject?.children?.length > 0) {
      return [...currentProject.children, currentProject]
    } else {
      return [currentProject]
    }
  }
})

const params = computed(() => store.getters['search/getParams'])
const mapParams = computed(() => {
  const zoom = params.value.find((param) => param.key === 'zoom')
  const centerLat = params.value.find((param) => param.key === 'center_lat')
  const centerLng = params.value.find((param) => param.key === 'center_lng')
  return zoom && centerLat && centerLng ? {
    center: [centerLng.value, centerLat.value],
    zoom: zoom.value
  } : null
})

// Publish the global instance so the ~36 geo/getMap consumers + the sidebar
// layer-injection chain keep working exactly as before.
const onReady = (instance) => {
  store.commit('geo/SET_MAP', instance)
  store.dispatch('geo/mapReady', true)
}

// Reposition on search param change (e.g. navigating to different coords).
watch(() => mapParams.value, (newParams) => {
  const map = store.getters['geo/getMap']
  if (newParams && map) {
    map.jumpTo({ center: newParams.center, zoom: newParams.zoom })
  }
})
</script>

<template>
  <div class="relative min-h-full w-full h-full bg-slate-100 overflow-hidden">
    <MapCanvas
      :map="activeMap"
      instance-key="__global__"
      :initial-options="mapParams"
      @ready="onReady"
      v-slot="{ instance }"
    >
      <MapProjectBoundaries :map="instance" :active-tab="activeTab" />

      <MapSource
        :map="instance"
        type="geojson"
        :id="'empty-source'"
        :data="{ type: 'FeatureCollection', features: [] }"
      >
        <MapLayer :map="instance" :id="'default-layer'" :source="'empty-source'" type="fill">
          <MapLayer
            :map="instance"
            :id="'project-layer'"
            :source="'empty-source'"
            type="fill"
            :before-id="'default-layer'"
          >
            <div v-if="projectsToRender && projectsToRender.length && activeTab === 'projects'" :key="projectsToRender.length">
              <MapProjectLayer
                v-for="project in projectsToRender"
                :map="instance"
                :key="project"
                :project="project"
                :active-tab="activeTab"
              />
            </div>
          </MapLayer>
        </MapLayer>
      </MapSource>

      <MapDraw :map="instance">
        <div class="absolute z-30 bottom-16 md:bottom-12 mb-3 md:mb-1 right-10 py-2 px-3" :class="{ 'mt-9': !!projectId }">
          <div class="flex items-end justify-end">
            <DialogCreate :is-layers-view="activeTab === 'layers'" />
          </div>
        </div>
      </MapDraw>
    </MapCanvas>
  </div>
</template>

<style>
.slideRight-enter-active, .slideRight-leave-active {
  transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
}
.slideRight-enter-from, .slideRight-leave-to {
  transform: translateX(-3%);
  opacity: 0;
}
</style>
